import { ReactNode } from "react";
import { ArrowLeft, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface FioriPageHeaderProps {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  count?: number;
  onBack?: () => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  actions?: ReactNode;
  showDivider?: boolean;
}

/**
 * SAP Fiori-style Object Page Header
 * - Flat white background with bottom border
 * - Title + subtitle + optional item count
 * - Back navigation for detail/form pages
 * - Right-aligned action buttons
 * - Fully responsive: wraps on mobile
 */
export function FioriPageHeader({
  title,
  subtitle,
  icon,
  count,
  onBack,
  onRefresh,
  isRefreshing = false,
  actions,
  showDivider = true,
}: FioriPageHeaderProps) {
  return (
    <div className={`bg-white py-4 md:py-5 mb-5 md:mb-6 ${showDivider ? "border-b border-[#E0E0E0]" : ""}`}>
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2 md:gap-3 min-w-0">
          {onBack && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-[#6B6B6B] hover:text-[#5B2C93] hover:bg-[#E8DCF5] flex-shrink-0"
              onClick={onBack}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
          )}
          {icon && (
            <div className="h-8 w-8 md:h-9 md:w-9 rounded-lg bg-[#E8DCF5] flex items-center justify-center text-[#5B2C93] flex-shrink-0">
              {icon}
            </div>
          )}
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-lg md:text-2xl font-semibold text-[#2C2C2C] leading-7 md:leading-8 truncate">{title}</h1>
              {count !== undefined && (
                <span className="inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-[#E8DCF5] text-[#5B2C93] text-xs font-medium flex-shrink-0">
                  {count}
                </span>
              )}
            </div>
            {subtitle && (
              <p className="text-sm md:text-sm text-[#6B6B6B] mt-1 truncate">{subtitle}</p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1.5 md:gap-2 flex-wrap justify-end">
          {onRefresh && (
            <Button
              variant="outline"
              size="sm"
              onClick={onRefresh}
              disabled={isRefreshing}
              className="gap-1.5 text-[#6B6B6B] border-[#E0E0E0] hover:border-[#5B2C93] hover:text-[#5B2C93] h-8 text-xs md:text-sm disabled:opacity-60"
            >
              <RefreshCw className={cn("h-3.5 w-3.5", isRefreshing && "animate-spin")} />
              <span className="hidden sm:inline">{isRefreshing ? "Refreshing..." : "Refresh"}</span>
            </Button>
          )}
          {actions}
        </div>
      </div>
    </div>
  );
}
